import { useMemo, useRef, useState } from "react";
import type { DateRange, Matcher } from "react-day-picker";

import { InputIcon, TextInput } from "@ui/components/Inputs";
import { useOutsideClick } from "@ui/hooks/useOutsideClick";
import { useTranslations } from "@ui/providers/useTranslations";
import { formatDate } from "@ui/utils/formatDate/formatDate";
import classNames from "classnames";

import { CalendarIcon } from "@jti/brand-icons/calendar";
import { Button } from "@jti/core-components/components/Button";

import { Calendar } from "./components/Calendar/Calendar";

import { isSameCalendarDay } from "./isSameCalendarDay";

import styles from "./DateRangePicker.module.scss";

export type DateRangePickerProps = {
  selected?: DateRange;
  onSaveSelection?: (range: DateRange | undefined) => void;
  onSelect?: (range: DateRange | undefined) => void;
  dropdownAlignment?: "left" | "right";
  placeholder: string;
  min?: number;
  max?: number;
  disabled?: Matcher | Matcher[];
  defaultMonth?: Date;
  dataTestId?: string;
  className?: string;
  saveButtonLabel?: string;
  applyOnClose?: boolean;
};

export const DateRangePicker = ({
  selected,
  onSaveSelection,
  onSelect,
  dropdownAlignment = "left",
  placeholder,
  min,
  max,
  disabled,
  defaultMonth,
  dataTestId = "dateRangePicker",
  className,
  saveButtonLabel,
  applyOnClose = false,
}: DateRangePickerProps) => {
  const { __ } = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(selected);

  const handleClose = () => {
    if (!isOpen) {
      return;
    }

    if (applyOnClose) {
      onSaveSelection?.(draftRange);
    }

    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen((prev) => {
      if (!prev) {
        setDraftRange(selected);
      } else if (applyOnClose) {
        onSaveSelection?.(draftRange);
      }
      return !prev;
    });
  };

  const handleSelect = (range: DateRange | undefined, immediate?: boolean) => {
    setDraftRange(range);
    onSelect?.(range);
    if (immediate) {
      onSaveSelection?.(range);
    }
  };

  const handleSave = () => {
    onSaveSelection?.(draftRange);
    setIsOpen(false);
  };

  useOutsideClick(containerRef, () => {
    handleClose();
  });

  const formattedRange = useMemo(() => {
    if (!selected?.from) return "";
    const fromStr = formatDate(selected.from);

    if (!selected.to || isSameCalendarDay(selected.from, selected.to)) return fromStr;

    return `${fromStr} - ${formatDate(selected.to)}`;
  }, [selected]);

  const displaySaveLabel = saveButtonLabel ?? __("date_range_picker.save_selection");

  const renderContent = (numberOfMonths: number, { immediateSave }: { immediateSave?: boolean } = {}) => (
    <>
      <div
        className={styles.trigger}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
      >
        <TextInput
          id="date-range-picker-trigger"
          role="combobox"
          aria-expanded={isOpen}
          value={formattedRange}
          placeholder={placeholder}
          elemAfter={<InputIcon icon={CalendarIcon} isDisabled />}
          containerClassName={classNames(styles.triggerInput, { [styles.activeInput]: isOpen })}
          data-testid={`${dataTestId}-trigger`}
        />
      </div>
      {isOpen && (
        <div
          className={classNames("light-theme", styles.dropdown, {
            [styles.dropdownRightAligned]: dropdownAlignment === "right",
          })}
        >
          <Calendar
            mode="range"
            selected={draftRange}
            onSelect={(range) => handleSelect(range, immediateSave)}
            numberOfMonths={numberOfMonths}
            min={min}
            max={max}
            disabled={disabled}
            defaultMonth={defaultMonth}
          />
          {!immediateSave && !applyOnClose && (
            <Button variant="secondary" size="md" onClick={handleSave} className={styles.saveButton}>
              {displaySaveLabel}
            </Button>
          )}
        </div>
      )}
    </>
  );

  return (
    <div ref={containerRef} className={classNames(styles.container, className)} data-testid={dataTestId}>
      <div className={styles.mobile}>{renderContent(1, { immediateSave: true })}</div>
      <div className={styles.desktopTablet}>{renderContent(2)}</div>
    </div>
  );
};
